// CRM Performance: one client's outbound performance in a single screen. Replaces
// the old Metrics placeholder. Email figures come from the existing metrics
// endpoints in mailbox-sender.js (unchanged), LinkedIn and platform cost come from
// performance.js. Cost is OUR spend, not the client's: the panel only appears if
// the cost endpoint answers, which it does for operator accounts only, and it is
// deliberately excluded from Raptor's client update.
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import toast from 'react-hot-toast';
import MDEditor from '@uiw/react-md-editor';
import API_BASE from '../../config';
import raptorIcon from '../../assets/raptor.svg';
import raptorMark from '../../assets/raptor-white.svg';

const RANGES = [
  { key: '7', label: '7 days', days: 7 },
  { key: '30', label: '30 days', days: 30 },
  { key: '90', label: '90 days', days: 90 },
  { key: 'life', label: 'Lifetime', days: null },
];

const TABS = [
  { key: 'email', label: 'Email' },
  { key: 'linkedin', label: 'LinkedIn' },
  { key: 'replies', label: 'Replies' },
  { key: 'deliverability', label: 'Deliverability' },
];

const RUNG_LABELS = {
  interest: 'Interest',
  info_request: 'Asked for information',
  referral: 'Referred on',
  timing: 'Wrong timing',
  decline: 'Declined',
  unclear: 'Unclear',
};

const num = v => (v === null || v === undefined || Number.isNaN(Number(v)) ? 0 : Number(v));
const pct = v => `${Math.round(num(v) * 10) / 10}%`;
const usd = v => (v === null || v === undefined ? 'n/a' : `$${Number(v).toFixed(2)}`);
const int = v => num(v).toLocaleString('en-GB');
const shortDay = d => {
  if (!d) return '';
  const dt = new Date(d);
  return `${dt.getDate()} ${dt.toLocaleString('en-GB', { month: 'short' })}`;
};
const shortMonth = m => {
  if (!m) return '';
  const dt = new Date(m);
  return `${dt.toLocaleString('en-GB', { month: 'short' })} ${String(dt.getFullYear()).slice(2)}`;
};

function Tile({ label, value, sub, tone }) {
  return (
    <div className={'crmpf-tile' + (tone ? ' crmpf-tile--' + tone : '')}>
      <div className="crmpf-tile-label">{label}</div>
      <div className="crmpf-tile-value">{value}</div>
      {sub ? <div className="crmpf-tile-sub">{sub}</div> : null}
    </div>
  );
}

function Empty({ children }) {
  return <div className="crmpf-empty">{children}</div>;
}

// Bars for volume, a line for replies. Deliberately plain: no chart library.
function VolumeChart({ rows, xKey, sentKey, replyKey, bounceKey }) {
  const W = 760;
  const H = 210;
  const PAD = { t: 14, r: 16, b: 26, l: 38 };
  if (!rows || !rows.length) return <Empty>No activity in this period.</Empty>;
  const iw = W - PAD.l - PAD.r;
  const ih = H - PAD.t - PAD.b;
  const maxSent = Math.max(1, ...rows.map(r => num(r[sentKey])));
  const maxReply = Math.max(1, ...rows.map(r => num(r[replyKey])));
  const bw = Math.max(2, Math.min(26, (iw / rows.length) * 0.6));
  const x = i => PAD.l + (iw / rows.length) * (i + 0.5);
  const ySent = v => PAD.t + ih - (num(v) / maxSent) * ih;
  const yReply = v => PAD.t + ih - (num(v) / maxReply) * ih;
  const replyPath = rows.map((r, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${yReply(r[replyKey]).toFixed(1)}`).join(' ');
  const labelEvery = Math.max(1, Math.ceil(rows.length / 10));
  return (
    <div className="crmpf-chart">
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" role="img" aria-label="Volume over time">
        {[0, 0.5, 1].map(f => (
          <line key={f} x1={PAD.l} x2={W - PAD.r} y1={PAD.t + ih * f} y2={PAD.t + ih * f} stroke="#EDEDEA" strokeWidth="1" />
        ))}
        <text x={PAD.l - 6} y={PAD.t + 4} textAnchor="end" className="crmpf-axis">{int(maxSent)}</text>
        <text x={PAD.l - 6} y={PAD.t + ih + 4} textAnchor="end" className="crmpf-axis">0</text>
        {rows.map((r, i) => {
          const h = PAD.t + ih - ySent(r[sentKey]);
          return (
            <rect key={'s' + i} x={x(i) - bw / 2} y={ySent(r[sentKey])} width={bw} height={Math.max(0, h)} fill="#D9DBD6" rx="1.5">
              <title>{`${shortDay(r[xKey]) || r[xKey]}: ${int(r[sentKey])} sent`}</title>
            </rect>
          );
        })}
        {bounceKey && rows.some(r => num(r[bounceKey]) > 0) ? (
          <path d={rows.map((r, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${yReply(r[bounceKey]).toFixed(1)}`).join(' ')} fill="none" stroke="#C4442F" strokeWidth="1.5" strokeDasharray="3 3" />
        ) : null}
        <path d={replyPath} fill="none" stroke="#FF7A59" strokeWidth="2" />
        {rows.map((r, i) => (
          <circle key={'r' + i} cx={x(i)} cy={yReply(r[replyKey])} r="2.6" fill="#FF7A59">
            <title>{`${shortDay(r[xKey]) || r[xKey]}: ${int(r[replyKey])} replies`}</title>
          </circle>
        ))}
        {rows.map((r, i) => (i % labelEvery === 0 ? (
          <text key={'x' + i} x={x(i)} y={H - 8} textAnchor="middle" className="crmpf-axis">{rows.length > 40 ? shortMonth(r[xKey]) : (String(r[xKey]).length === 7 ? shortMonth(r[xKey]) : shortDay(r[xKey]))}</text>
        ) : null))}
      </svg>
      <div className="crmpf-legend">
        <span><i className="crmpf-sw" style={{ background: '#D9DBD6' }} />Sent</span>
        <span><i className="crmpf-sw" style={{ background: '#FF7A59' }} />Replies</span>
        {bounceKey ? <span><i className="crmpf-sw" style={{ background: '#C4442F' }} />Bounces</span> : null}
      </div>
    </div>
  );
}

export default function CrmPerformance() {
  const { id } = useParams();
  const { getToken } = useAuth();
  const [searchParams] = useSearchParams();
  // The palette can hand a question over by URL, including when this screen is
  // already open, so follow the parameter rather than reading it once on mount.
  const askParam = searchParams.get('ask') || '';
  const cid = id;

  const [range, setRange] = useState('30');
  const [tab, setTab] = useState('email');
  const [loading, setLoading] = useState(true);

  const [overview, setOverview] = useState(null);
  const [series, setSeries] = useState([]);
  const [lifetime, setLifetime] = useState(null);
  const [funnel, setFunnel] = useState([]);
  const [intent, setIntent] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [mailboxes, setMailboxes] = useState([]);
  const [li, setLi] = useState(null);
  const [cost, setCost] = useState(null);

  const [askText, setAskText] = useState(searchParams.get('ask') || '');
  const [thread, setThread] = useState([]);
  const [asking, setAsking] = useState(false);
  const [draft, setDraft] = useState('');
  const [drafting, setDrafting] = useState(false);

  useEffect(() => { if (askParam) setAskText(askParam); }, [askParam]);

  const authFetch = useCallback(async (path, opts = {}) => {
    const token = await getToken();
    return fetch(`${API_BASE}${path}`, { ...opts, headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...(opts.headers || {}) } });
  }, [getToken]);

  const days = useMemo(() => (RANGES.find(r => r.key === range) || RANGES[1]).days, [range]);

  // Email tiles, funnel and reply intent are the existing 30-day metrics and are
  // labelled as such. Only the chart and the LinkedIn/cost views follow the picker.
  useEffect(() => {
    let dead = false;
    (async () => {
      try {
        const [o, f, ri, cs, mb] = await Promise.all([
          fetch(`${API_BASE}/clients/${cid}/metrics/overview`).then(r => (r.ok ? r.json() : null)).catch(() => null),
          fetch(`${API_BASE}/clients/${cid}/metrics/funnel`).then(r => (r.ok ? r.json() : [])).catch(() => []),
          fetch(`${API_BASE}/clients/${cid}/metrics/reply-intent`).then(r => (r.ok ? r.json() : null)).catch(() => null),
          fetch(`${API_BASE}/clients/${cid}/metrics/campaigns`).then(r => (r.ok ? r.json() : [])).catch(() => []),
          fetch(`${API_BASE}/clients/${cid}/metrics/mailboxes`).then(r => (r.ok ? r.json() : [])).catch(() => []),
        ]);
        if (dead) return;
        setOverview(o);
        setFunnel(Array.isArray(f) ? f : []);
        setIntent(ri);
        setCampaigns(Array.isArray(cs) ? cs : []);
        setMailboxes(Array.isArray(mb) ? mb : []);
      } finally {
        if (!dead) setLoading(false);
      }
    })();
    return () => { dead = true; };
  }, [cid]);

  useEffect(() => {
    let dead = false;
    (async () => {
      const r = await authFetch(`/clients/${cid}/performance/email-lifetime`).catch(() => null);
      if (dead || !r || !r.ok) return;
      setLifetime(await r.json());
    })();
    return () => { dead = true; };
  }, [cid, authFetch]);

  useEffect(() => {
    let dead = false;
    (async () => {
      if (days === null) return;
      const r = await fetch(`${API_BASE}/clients/${cid}/metrics/timeseries?days=${days}`).catch(() => null);
      if (dead || !r || !r.ok) return;
      const rows = await r.json();
      setSeries(Array.isArray(rows) ? rows : []);
    })();
    return () => { dead = true; };
  }, [cid, days]);

  useEffect(() => {
    let dead = false;
    (async () => {
      const q = days === null ? '?days=3650' : `?days=${days}`;
      const [l, c] = await Promise.all([
        authFetch(`/clients/${cid}/performance/linkedin${q}`).catch(() => null),
        authFetch(`/clients/${cid}/performance/cost${q}`).catch(() => null),
      ]);
      if (dead) return;
      if (l && l.ok) setLi(await l.json());
      if (c && c.ok) setCost(await c.json());
      else setCost(null);
    })();
    return () => { dead = true; };
  }, [cid, days, authFetch]);

  const chartRows = days === null ? (lifetime && lifetime.monthly ? lifetime.monthly.map(m => ({ day: m.month, sent: m.sent, replies: m.replies, bounces: m.bounces })) : []) : series;

  const sentDelta = useMemo(() => {
    if (!overview) return null;
    const a = num(overview.sent_30d);
    const b = num(overview.sent_prev_30d);
    if (!b) return null;
    const d = Math.round(((a - b) / b) * 1000) / 10;
    return `${d >= 0 ? '+' : ''}${d}% on previous 30 days`;
  }, [overview]);

  // Exactly what is on screen, so Raptor talks about these numbers and no others.
  // Cost is deliberately absent: the server also strips anything money-shaped.
  const snapshot = useMemo(() => ({
    window: days === null ? 'all time' : `last ${days} days`,
    email_30d: overview ? { sent: overview.sent_30d, sent_prev_30d: overview.sent_prev_30d, replies: overview.replies_30d, reply_rate: overview.reply_rate_30d, bounce_rate: overview.bounce_rate_30d, deliverability_score: overview.deliverability_score, active_mailboxes: overview.active_mailboxes } : null,
    email_lifetime: lifetime ? { sent: lifetime.sent, replies: lifetime.replies, bounces: lifetime.bounces, reply_rate: lifetime.reply_rate, first_activity: lifetime.first_activity, last_activity: lifetime.last_activity } : null,
    sequence_steps: funnel,
    reply_intent: intent ? { total: intent.total, classified: intent.classified, interested_rate: intent.interested_rate, breakdown: intent.breakdown } : null,
    campaigns: campaigns.slice(0, 15).map(c => ({ name: c.name, status: c.status, sent: c.sent_count, replies: c.reply_count, reply_rate: c.reply_rate })),
    linkedin: li ? { funnel: li.funnel, rungs: li.rungs, campaigns: li.campaigns, validated: li.validated } : null,
  }), [days, overview, lifetime, funnel, intent, campaigns, li]);

  async function writeUpdate() {
    setDrafting(true);
    try {
      const r = await authFetch(`/clients/${cid}/performance/update`, { method: 'POST', body: JSON.stringify({ days, snapshot }) });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || 'Could not draft the update');
      setDraft(j.text || '');
    } catch (e) {
      toast.error(e.message);
    } finally {
      setDrafting(false);
    }
  }

  async function ask() {
    const q = askText.trim();
    if (!q || asking) return;
    setAskText('');
    setThread(t => [...t, { role: 'user', text: q }]);
    setAsking(true);
    try {
      const r = await authFetch(`/clients/${cid}/performance/ask`, { method: 'POST', body: JSON.stringify({ question: q, days, snapshot, cost }) });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || 'Raptor could not answer');
      setThread(t => [...t, { role: 'raptor', text: j.answer || '' }]);
    } catch (e) {
      setThread(t => [...t, { role: 'raptor', text: `Could not answer: ${e.message}` }]);
    } finally {
      setAsking(false);
    }
  }

  function copyDraft() {
    if (!draft) return;
    navigator.clipboard.writeText(draft).then(() => toast.success('Copied'), () => toast.error('Could not copy'));
  }

  const rungs = (li && li.rungs) || [];
  const liFunnel = (li && li.funnel) || null;

  return (
    <div className="crmcl crmpf">
      <div className="crmpf-head">
        <div>
          <h1 className="crmpf-title">Performance</h1>
          <p className="crmpf-sub">What went out, what came back, and what it proved.</p>
        </div>
        <div className="crmpf-ranges">
          {RANGES.map(r => (
            <button key={r.key} className={'crmpf-range' + (range === r.key ? ' crmpf-range--on' : '')} onClick={() => setRange(r.key)}>{r.label}</button>
          ))}
        </div>
      </div>

      <div className="crmpf-grid">
        {/* LEFT: headline numbers */}
        <aside className="crmpf-col crmpf-left">
          <div className="crmpf-secthead">Email, last 30 days</div>
          <Tile label="Sent" value={loading ? '–' : int(overview && overview.sent_30d)} sub={sentDelta} />
          <Tile label="Replies" value={loading ? '–' : int(overview && overview.replies_30d)} sub={overview ? `${pct(overview.reply_rate_30d)} reply rate` : null} tone="accent" />
          <Tile label="Bounce rate" value={loading ? '–' : pct(overview && overview.bounce_rate_30d)} sub={overview ? `Deliverability ${int(overview.deliverability_score)}/100` : null} />
          <Tile label="Mailboxes" value={loading ? '–' : int(overview && overview.active_mailboxes)} sub={overview && num(overview.domains_total) > 0 ? `${int(overview.domains_healthy)} of ${int(overview.domains_total)} domains healthy` : null} />

          {lifetime && num(lifetime.sent) > 0 ? (
            <>
              <div className="crmpf-secthead">Email, lifetime</div>
              <Tile label="Sent" value={int(lifetime.sent)} sub={lifetime.first_activity ? `Since ${shortDay(lifetime.first_activity)} ${new Date(lifetime.first_activity).getFullYear()}` : null} />
              <Tile label="Replies" value={int(lifetime.replies)} sub={`${pct(lifetime.reply_rate)} reply rate`} tone="accent" />
            </>
          ) : null}

          {liFunnel && num(liFunnel.contacted) > 0 ? (
            <>
              <div className="crmpf-secthead">LinkedIn</div>
              <Tile label="Invited" value={int(liFunnel.invited)} sub={`${int(liFunnel.accepted)} accepted, ${pct(liFunnel.accept_rate)}`} />
              <Tile label="Replies" value={int(liFunnel.replied)} sub={`${pct(liFunnel.reply_rate)} of those messaged`} tone="accent" />
            </>
          ) : null}

          {cost ? (
            <>
              <div className="crmpf-secthead crmpf-secthead--op">Platform cost <span className="crmpf-op">Operator only</span></div>
              <Tile label="Our spend" value={usd(cost.total_usd)} sub={`${int(cost.calls)} model and search calls`} />
              <Tile label="Per graded reply" value={usd(cost.cost_per_graded_reply)} sub={`${int(cost.graded_replies)} graded replies`} />
              <p className="crmpf-opnote">Our cost of running this client, in USD. Never shown to the client and left out of the client update.</p>
            </>
          ) : null}
        </aside>

        {/* CENTRE: detail */}
        <section className="crmpf-col crmpf-centre">
          <div className="crmpf-tabs">
            {TABS.map(t => (
              <button key={t.key} className={'crmpf-tab' + (tab === t.key ? ' crmpf-tab--on' : '')} onClick={() => setTab(t.key)}>{t.label}</button>
            ))}
          </div>

          {tab === 'email' ? (
            <>
              <div className="crmpf-card">
                <div className="crmpf-cardhead">
                  <h2>Volume and replies</h2>
                  <span className="crmpf-muted">{days === null ? 'By month, all time' : `Last ${days} days`}</span>
                </div>
                <VolumeChart rows={chartRows} xKey="day" sentKey="sent" replyKey="replies" bounceKey="bounces" />
              </div>

              <div className="crmpf-card">
                <div className="crmpf-cardhead"><h2>Sequence steps</h2><span className="crmpf-muted">Last 30 days</span></div>
                {funnel.length ? (
                  <table className="crmpf-table">
                    <thead><tr><th>Step</th><th className="crmpf-r">Sent</th><th className="crmpf-r">Replied</th><th className="crmpf-r">Reply rate</th></tr></thead>
                    <tbody>
                      {funnel.map(f => (
                        <tr key={f.step}>
                          <td>Step {f.step}</td>
                          <td className="crmpf-r">{int(f.sent)}</td>
                          <td className="crmpf-r">{int(f.replied)}</td>
                          <td className="crmpf-r">{pct(f.reply_rate)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : <Empty>Nothing sent in the last 30 days.</Empty>}
              </div>

              <div className="crmpf-card">
                <div className="crmpf-cardhead"><h2>Campaigns</h2><span className="crmpf-muted">{int(campaigns.length)} total</span></div>
                {campaigns.length ? (
                  <table className="crmpf-table">
                    <thead><tr><th>Campaign</th><th>Status</th><th className="crmpf-r">Sent</th><th className="crmpf-r">Replies</th><th className="crmpf-r">Reply rate</th><th className="crmpf-r">Done</th></tr></thead>
                    <tbody>
                      {campaigns.slice(0, 25).map(c => (
                        <tr key={c.id}>
                          <td className="crmpf-strong">{c.name || 'Untitled'}</td>
                          <td><span className={'crmpf-pill crmpf-pill--' + (c.status || 'draft')}>{c.status || 'draft'}</span></td>
                          <td className="crmpf-r">{int(c.sent_count)}</td>
                          <td className="crmpf-r">{int(c.reply_count)}</td>
                          <td className="crmpf-r">{pct(c.reply_rate)}</td>
                          <td className="crmpf-r">{int(c.completion_pct)}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : <Empty>No campaigns yet.</Empty>}
              </div>
            </>
          ) : null}

          {tab === 'linkedin' ? (
            <>
              <div className="crmpf-card">
                <div className="crmpf-cardhead"><h2>LinkedIn funnel</h2><span className="crmpf-muted">{days === null ? 'All time' : `Last ${days} days`}</span></div>
                {liFunnel ? (
                  <div className="crmpf-rungs">
                    {[
                      { k: 'contacted', label: 'In a campaign', v: liFunnel.contacted, of: null },
                      { k: 'invited', label: 'Invited', v: liFunnel.invited, of: liFunnel.contacted },
                      { k: 'accepted', label: 'Accepted', v: liFunnel.accepted, of: liFunnel.invited },
                      { k: 'messaged', label: 'Messaged', v: liFunnel.messaged, of: liFunnel.accepted },
                      { k: 'replied', label: 'Replied', v: liFunnel.replied, of: liFunnel.messaged },
                      { k: 'graded', label: 'Graded reply', v: liFunnel.graded, of: liFunnel.replied },
                    ].map(s => (
                      <div key={s.k} className="crmpf-rung">
                        <div className="crmpf-rung-bar" style={{ width: `${liFunnel.contacted ? Math.max(4, (num(s.v) / Math.max(1, num(liFunnel.contacted))) * 100) : 4}%` }} />
                        <div className="crmpf-rung-body">
                          <span className="crmpf-rung-label">{s.label}</span>
                          <span className="crmpf-rung-val">{int(s.v)}{s.of ? <em> {pct(num(s.of) ? (num(s.v) / num(s.of)) * 100 : 0)} of previous</em> : null}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : <Empty>No LinkedIn activity recorded for this client.</Empty>}
              </div>

              <div className="crmpf-card">
                <div className="crmpf-cardhead"><h2>By campaign</h2></div>
                {li && li.campaigns && li.campaigns.length ? (
                  <table className="crmpf-table">
                    <thead><tr><th>Campaign</th><th className="crmpf-r">In</th><th className="crmpf-r">Accepted</th><th className="crmpf-r">Replied</th><th className="crmpf-r">Reply rate</th></tr></thead>
                    <tbody>
                      {li.campaigns.map(c => (
                        <tr key={c.id || c.name}>
                          <td className="crmpf-strong">{c.name || 'Unnamed campaign'}</td>
                          <td className="crmpf-r">{int(c.contacted)}</td>
                          <td className="crmpf-r">{int(c.accepted)}</td>
                          <td className="crmpf-r">{int(c.replied)}</td>
                          <td className="crmpf-r">{pct(c.reply_rate)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : <Empty>No LinkedIn campaigns have reported in yet.</Empty>}
              </div>
            </>
          ) : null}

          {tab === 'replies' ? (
            <>
              <div className="crmpf-card">
                <div className="crmpf-cardhead"><h2>What the email replies said</h2><span className="crmpf-muted">Last 30 days</span></div>
                {intent && intent.breakdown && intent.breakdown.length ? (
                  <>
                    <div className="crmpf-bars">
                      {intent.breakdown.map(b => (
                        <div key={b.label} className="crmpf-barrow">
                          <span className="crmpf-barlabel">{b.label}</span>
                          <span className="crmpf-bartrack"><i style={{ width: `${Math.max(2, num(b.pct))}%` }} /></span>
                          <span className="crmpf-barval">{int(b.count)} · {pct(b.pct)}</span>
                        </div>
                      ))}
                    </div>
                    <p className="crmpf-foot">{int(intent.classified)} of {int(intent.total)} replies classified. Interested: {pct(intent.interested_rate)}.</p>
                  </>
                ) : <Empty>No classified replies yet.</Empty>}
              </div>

              <div className="crmpf-card">
                <div className="crmpf-cardhead"><h2>What the LinkedIn replies proved</h2><span className="crmpf-muted">Graded against the market graph</span></div>
                {rungs.length ? (
                  <>
                    <div className="crmpf-bars">
                      {rungs.map(r => (
                        <div key={r.rung} className="crmpf-barrow">
                          <span className="crmpf-barlabel">{RUNG_LABELS[r.rung] || r.rung}</span>
                          <span className="crmpf-bartrack"><i style={{ width: `${Math.max(2, num(r.pct))}%` }} /></span>
                          <span className="crmpf-barval">{int(r.count)} · {pct(r.pct)}</span>
                        </div>
                      ))}
                    </div>
                    {li && li.validated ? (
                      <p className="crmpf-foot">{int(li.validated.relationships)} offering and segment pairings now carry evidence from this client{li.validated.segments ? `, across ${int(li.validated.segments)} segments` : ''}.</p>
                    ) : null}
                  </>
                ) : <Empty>No graded replies yet. Grading happens when a reply reaches the inbound webhook.</Empty>}
              </div>
            </>
          ) : null}

          {tab === 'deliverability' ? (
            <div className="crmpf-card">
              <div className="crmpf-cardhead"><h2>Mailboxes</h2><span className="crmpf-muted">Last 30 days</span></div>
              {mailboxes.length ? (
                <table className="crmpf-table">
                  <thead><tr><th>Mailbox</th><th>Status</th><th className="crmpf-r">Sent</th><th className="crmpf-r">Replies</th><th className="crmpf-r">Bounce rate</th><th className="crmpf-r">Score</th></tr></thead>
                  <tbody>
                    {mailboxes.map(m => (
                      <tr key={m.id}>
                        <td className="crmpf-strong">{m.email || m.name || '–'}</td>
                        <td><span className={'crmpf-pill crmpf-pill--' + (m.status || 'paused')}>{m.status || 'paused'}</span></td>
                        <td className="crmpf-r">{int(m.sent_30d)}</td>
                        <td className="crmpf-r">{int(m.replies_30d)}</td>
                        <td className="crmpf-r">{pct(m.bounce_rate)}</td>
                        <td className="crmpf-r">{int(m.deliverability_score)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : <Empty>No mailboxes connected for this client.</Empty>}
            </div>
          ) : null}
        </section>

        {/* RIGHT: Raptor */}
        <aside className="crmpf-col crmpf-right">
          <div className="crmpf-raptor">
            <div className="crmpf-raptor-head">
              <span className="crmh-panel-mark"><img src={raptorMark} alt="Raptor" /></span>
              <div>
                <strong>Raptor</strong>
                <span className="crmpf-muted">Reads these numbers with you</span>
              </div>
            </div>

            <button className="crmpf-primary" onClick={writeUpdate} disabled={drafting}>
              {drafting ? 'Writing' : 'Write the client update'}
            </button>

            {draft ? (
              <div className="crmpf-draft">
                <div className="crmpf-drafthead">
                  <span>Client update</span>
                  <span className="crmpf-draftacts">
                    <button onClick={copyDraft}>Copy</button>
                    <button onClick={() => setDraft('')}>Clear</button>
                  </span>
                </div>
                <textarea className="crmpf-drafttext" value={draft} onChange={e => setDraft(e.target.value)} rows={16} spellCheck />
                <p className="crmpf-foot">Edit before sending. Our platform cost is not in here.</p>
              </div>
            ) : null}

            <div className="crmpf-thread">
              {thread.length === 0 ? (
                <div className="crmpf-raptor-intro">
                  <div className="crmh-intro-mark"><img src={raptorMark} alt="Raptor" /></div>
                  <p>Ask about the numbers on this screen. For example, which step is losing people, or whether the reply rate is holding up against the previous period.</p>
                </div>
              ) : thread.map((m, i) => (
                <div key={i} className={'crmpf-msg crmpf-msg--' + m.role}>
                  {m.role === 'raptor'
                    ? <div className="crmpf-md" data-color-mode="light"><MDEditor.Markdown source={m.text} /></div>
                    : <p>{m.text}</p>}
                </div>
              ))}
              {asking ? <div className="crmpf-msg crmpf-msg--raptor"><p className="crmpf-muted">Thinking</p></div> : null}
            </div>

            <div className="crmpf-composer">
              <img className="crm-composer-raptor" src={raptorIcon} alt="Raptor" />
              <textarea
                value={askText}
                onChange={e => setAskText(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); ask(); } }}
                placeholder="Ask about this client's performance"
                rows={2}
              />
              <button onClick={ask} disabled={asking || !askText.trim()}>Ask</button>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
